// chatInput.tsx

'use client'

import React, { useState, useEffect, useRef } from 'react';
import { HiOutlinePhotograph } from 'react-icons/hi';
import { XCircleIcon } from '@heroicons/react/24/solid';
import { Spinner } from 'flowbite-react';

interface ChatInputProps {
    onSend: (message: string, file: File | null) => void;
    // The message state is still controlled by the parent for captioning
    message: string;
    onMessageChange: (value: string) => void;
    isSending: boolean; // Prop to know if a message is currently being sent
}

const ChatInput: React.FC<ChatInputProps> = ({ 
    onSend,
    message,
    onMessageChange,
    isSending,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Effect to clean up the object URL and prevent memory leaks
  useEffect(() => {
    // This is the cleanup function that runs when the component unmounts
    // or when the previewUrl changes.
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Create a temporary URL for the browser to display the image
      setPreviewUrl(URL.createObjectURL(file));
    }
  };
  
  const handleRemoveImage = () => {
    setSelectedFile(null);
    setPreviewUrl(null); // The useEffect will handle revoking the URL
    // Also reset the file input so the same file can be selected again
    if(fileInputRef.current) {
        fileInputRef.current.value = "";
    }
  };

  const handleSend = () => {
    // Don't send if already sending or if there's nothing to send
    if (isSending || (!message.trim() && !selectedFile)) return;
    
    onSend(message, selectedFile);
    // After calling onSend, we clean up the local state here.
    // The parent (`Messages.tsx`) is responsible for clearing its own `messageText`.
    handleRemoveImage();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevents adding a new line
      handleSend();
    }
  };

  return (
    <div className="bg-gray-200 dark:bg-gray-700 rounded-lg p-2">
      {/* Image Preview Area */}
      {previewUrl && (
        <div className="relative w-24 h-24 mb-2 p-1 border border-gray-300 rounded-lg">
          <img src={previewUrl} alt="Preview" className="w-full h-full object-cover rounded" />
          <button 
            type="button" 
            onClick={handleRemoveImage} 
            className="absolute -top-2 -right-2 bg-gray-700 rounded-full text-white hover:bg-red-500"
            aria-label="Remove image"
          >
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>
      )}

      {/* Input and Buttons Area */}
      <div className="flex items-center">
        <label className="cursor-pointer p-2 text-gray-500 rounded-lg hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-600">
          <HiOutlinePhotograph className="w-6 h-6" />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png, image/jpeg, image/jpg"
            onChange={handleFileSelect}
            className="hidden"
          />
        </label>
        <textarea
          id="chatInput"
          rows={1}
          value={message}
          onChange={(e) => onMessageChange(e.target.value)}
          onKeyDown={handleKeyDown}
          className="block mx-2 p-2.5 w-full text-base resize-none bg-white rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
          placeholder="Your message..."
          style={{maxHeight: '120px'}} // Prevent it from growing too tall
        ></textarea>
        <button
          type="button"
          onClick={handleSend}
          disabled={isSending || (!message.trim() && !selectedFile)}
          className="inline-flex justify-center p-2 text-blue-600 rounded-full cursor-pointer hover:bg-blue-100 dark:text-blue-500 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSending ? (
            <Spinner size="sm" />
          ) : (
            <svg className="w-6 h-6 rotate-90" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 18 20">
              <path d="m17.914 18.594-8-18a1 1 0 0 0-1.828 0l-8 18a1 1 0 0 0 1.157 1.376L8 18.281V9a1 1 0 0 1 2 0v9.281l6.758 1.689a1 1 0 0 0 1.156-1.376Z" />
            </svg>
          )}
          <span className="sr-only">Send message</span>
        </button>
      </div>
    </div>
  );
};

export default ChatInput;